import {
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  Type,
  ViewChild
} from '@angular/core';
import { NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PlayerMessageService } from '../../services/player.service';
import { PreviewApiService } from '../../services/preview-api.service';
import { WidgetMessageService } from '../../services/widget-message.service';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { PixlEditorComponent } from '@wdidev/pixl-editor';
import { Store } from '@ngxs/store';
import { AuthState } from '../../authentication/auth.state';
import { environment } from '../../../../environments/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AngularSplitModule } from 'angular-split';
import {
  Config,
  Correct,
  ExerciseSubKind,
  FreeInputTextPart,
  IFrameMessage,
  LearnItem,
  LearnItemPart,
  Pages,
  PlayerContentMessage,
  PlayerSettings,
  TextPart
} from '../../types/player-types';
import {
  FreeInputTextEditorComponent
} from '../../../lesson/exercise-type-editors/free-input-text-editor/free-input-text-editor.component';
import { ContentEditorService } from '../../services/content-editor.service';
import { getExplanationParts, getExplanationPartsByLayout, initialConfig, initialLearnItem, PARTS_BY_KIND } from '../../consts/player-consts';
import TurndownService from 'turndown';
import { Approval } from '../../types';
import { TranslationService } from '../../services/translationService';
import { getKind } from '../../util';
import { SpinnerComponent } from '../../spinner/spinner/spinner.component';
import { ExamEntity } from '../../../lesson/exam/exam.entity';
import { ExerciseEntity, QuantityError } from '../../../lesson/exercise/exercise.entity';
import { ExerciseEntityService } from '../../../lesson/exercise/services/exercise.entity.service';
import { ExamEntityService } from '../../../lesson/exam/services/exam.entity.service';
import { ExplanationEntity } from '../../../explanation/explanation-entity';
import { ExplanationEntityService } from '../../../explanation/services/explanation.entity.service';
import { from, Observable, switchMap, tap } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import {
  PagesDialogComponent,
  PagesDialogData
} from './pages-dialog/pages-dialog.component';

/**
 * Controls which exercise types show the Quantity Errors tab in the content editor.
 * Add a type here with `quantityErrors: false` to hide the tab for all exercises of that type.
 * Types not listed default to showing the tab.
 *
 * TODO: One day this should be implemented to be edited in the backend.
 */
const exerciseTypeConfig: Record<string, { quantityErrors?: boolean }> = {
  'freitext': { quantityErrors: false },
  'lsa-zusammenfassung': { quantityErrors: false },
  'lsa-frage-zum-text': { quantityErrors: false },
};

const DEFAULT_QUANTITY_ERROR_RANGES: QuantityError[] = [
  { from: 0, to: 24, message: '' },
  { from: 25, to: 49, message: '' },
  { from: 50, to: 74, message: '' },
  { from: 75, to: 100, message: '' },
];

export interface ContentEntityDialogData {
  type: 'exam' | 'exercise' | 'explanation'
  model: ExamEntity | ExerciseEntity | ExplanationEntity
}

const EXPLANATION_LAYOUT_TRANSITION: Record<string, Record<string, Record<string, string>>> = {
  '1col': {
    '1col_mCol': { 'content-l': 'content-m' },
    '2col': { 'content-l': 'content-double-left' },
  },
  '1col_mCol': {
    '1col': { 'content-m': 'content-l' },
    '2col': { 'content-m': 'content-double-left', 'content-s': 'content-double-right' },
  },
  '2col': {
    '1col': { 'content-double-left': 'content-l' },
    '1col_mCol': { 'content-double-left': 'content-m', 'content-double-right': 'content-s' },
  },
};

@Component({
  selector: 'pixl-exam-entity-view',
  standalone: true,
  imports: [
    MatDividerModule,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatDialogTitle,
    PixlEditorComponent,
    AngularSplitModule,
    SpinnerComponent,
    TranslocoModule,
    NgComponentOutlet,
    NgTemplateOutlet,
    MatIcon,
    MatTooltip,
    MatTabsModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatSelectModule,
  ],
  templateUrl: './content-entity.component.html',
  styleUrl: './content-entity.component.scss'
})
export class ContentEntityComponent implements OnInit, OnDestroy {
  @ViewChild('playerPreviewIframe') playerPreviewIframe!: ElementRef<HTMLIFrameElement>;
  @ViewChild(MatDialogContent, { read: ElementRef }) dialogContent!: ElementRef<HTMLElement>;

  private readonly dialogRef = inject(MatDialogRef<ContentEntityComponent>);
  private sanitizer = inject(DomSanitizer);
  private store = inject(Store);
  private auth$ = this.store.select(AuthState.getToken);
  private exam$!: Observable<ExamEntity[]>;
  private exercise$!: Observable<ExerciseEntity[]>;
  private explanation$!: Observable<ExplanationEntity[]>;
  private examcise!: ExamEntity | ExerciseEntity | ExplanationEntity;
  private explanationService = inject(ExplanationEntityService);
  private destroyRef = inject(DestroyRef);
  private turndownService = new TurndownService();
  private translationService = inject(TranslationService);
  private previewApiService = inject(PreviewApiService);
  private widgetMessageService = inject(WidgetMessageService);
  private translocoService = inject(TranslocoService);
  private examService = inject(ExamEntityService);
  private exerciseService = inject(ExerciseEntityService);
  private isPlayerMessageReady = signal(false);
  private contentEditorService = inject(ContentEditorService);

  public playerService = inject(PlayerMessageService);
  public dialogData = inject<ContentEntityDialogData>(MAT_DIALOG_DATA);
  public data: ExamEntity | ExerciseEntity | ExplanationEntity = this.dialogData.model;
  public iframePreviewUrl: SafeResourceUrl | null = null;
  public explanationContentHtml = '';
  public authToken: string | null = '';
  public assetsUrl: string = environment.apiUrl + '/assets';
  public mediaUrl: string = environment.assetsHost + '/';
  public editorPaneVisible = signal(false);
  public activeEditorComponent = signal<Type<unknown> | null>(null);
  public activeEditorInputs = signal<Record<string, unknown>>({});
  public taskName = signal('');
  public showAddExerciseButton = signal(false);
  public playerLoadError = signal(false);
  public accounting = signal(false);
  public quantityErrorRanges = signal<QuantityError[]>([]);
  public activeRangeIndex = signal<number | null>(null);
  public messageText = signal('');
  public currentPageIndex = signal<number>(0);
  private dialog = inject(MatDialog);

  public playerContentMessage: PlayerContentMessage = {
    config: initialConfig,
    editable: true,
    kind: 'Examcise',
    learnItem: initialLearnItem,
  };

  constructor() {
    this.playerService.messages$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(message => this.handlePlayerEvents(message));

    effect(() => {
      if (!this.editorPaneVisible()) {
        this.playerService.postMessageToPlayer(this.playerService.iFramePlayerPreviewUrl(this.data.consumerId), 'MarkdownEditorClosed');
      }
    });

    this.contentEditorService.partChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.postMessage2Player());
  }

  ngOnInit(): void {
    this.initQuantityErrors();
    let taskType = (this.data as ExamEntity | ExerciseEntity).kind ?? this.dialogData.type;
    if (this.data.config && this.data.config.exerciseSubKind) {
      taskType += `.${this.data.config.exerciseSubKind}`;
    }
    this.taskName.set(getKind(this.translationService, taskType));
    if (taskType === 'buchungstrainer') {
      this.accounting.set(true);
    }

    this.iframePreviewUrl = this.sanitizer
      .bypassSecurityTrustResourceUrl(this.playerService.iFramePlayerPreviewUrl(this.data.consumerId));

    this.auth$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((token: unknown) => {
        this.authToken = token as string | null;
      });

    const query: Record<string, string | number> = {
      'include': 'consumers,subjects',
      'filter[id]': this.data.id
    };

    switch (this.dialogData.type) {
      case 'exam':
        this.exam$ = from(
          this.examService.ensureLoaded(query)
        ).pipe(
          switchMap(() =>
            this.store.select(this.examService.allByQuery(query))
          ),
          tap(val => {
            this.examcise = val[0];
            if (this.examcise) {
              this.initMessage(this.examcise);
            }
          })
        );

        this.exam$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        break;
      case 'exercise':
        this.exercise$ = from(
          this.exerciseService.ensureLoaded(query)
        ).pipe(
          switchMap(() =>
            this.store.select(this.exerciseService.allByQuery(query))
          ),
          tap(val => {
            this.examcise = val[0];
            if (this.examcise) {
              this.initMessage(this.examcise);
            }
          })
        );

        this.exercise$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        break;
      case 'explanation':
        this.explanation$ = from(
          this.explanationService.ensureLoaded(query)
        ).pipe(
          switchMap(() =>
            this.store.select(this.explanationService.allByQuery(query))
          ),
          tap(val => {
            this.examcise = val[0];
            if (this.examcise) {
              this.initMessage(this.examcise);
            }
          })
        );

        this.explanation$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        break;
      default:
        console.error('::==> Unbekannter Dialogdatentyp <==::');
    }

    setTimeout(() => {
      if (!this.playerService.iFrameReady) {
        this.playerLoadError.set(true);
      }
    }, 10000);
  }

  ngOnDestroy(): void {
    this.playerService.pageIndex = -1;
    this.playerService.index = -1;
    this.playerService.iFrameReady = false;
  }

  private initMessage(examcise: ExamEntity | ExerciseEntity | ExplanationEntity): void {
    const kindKey = (examcise.config && examcise.config.exerciseSubKind) ?
      examcise.config.exerciseSubKind :
      ((examcise as ExamEntity | ExerciseEntity).kind ?? this.dialogData.type);

    const defaultPartsForKind = kindKey === 'explanation' ?
      getExplanationParts(this.translocoService) :
      PARTS_BY_KIND[kindKey];

    if (!defaultPartsForKind) {
      this.widgetMessageService.setMessage({
        message: this.translocoService.translate('task_type_undefined', { kind: kindKey }),
        messageTye: 'mtError'
      });
      this.onCancel();
      return;
    }

    let mappedPages: Pages[];
    const rawPages = (examcise.pages as unknown) as Record<string, unknown>[] | null;

    if (rawPages && rawPages.length > 0) {
      mappedPages = rawPages.map((page): Pages => {
        const rawParts = (page.parts as unknown) as Record<string, unknown>[] | null;

        return {
          _t: (page._t as Pages['_t']) || 'SimpleLearnItemPage',
          showMarginalColumnOnMobile: Boolean(page.showMarginalColumnOnMobile),
          baseLayout: (page.baseLayout as string | null) ?? null,
          layout: (page.layout as string) || 'horizontalLayout',
          config: (page.config as Record<string, unknown> | null) ?? {},
          parts: (rawParts && rawParts.length > 0) ?
            rawParts.map((part): LearnItemPart => {
              const { _t, ...rest } = part;
              return {
                ...rest,
                _t: _t as string,
              } as LearnItemPart;
            }) :
            structuredClone(defaultPartsForKind)
        };
      });
    }
    else {
      mappedPages = [
        {
          _t: 'SimpleLearnItemPage',
          showMarginalColumnOnMobile: false,
          baseLayout: null,
          layout: 'horizontalLayout',
          config: {},
          parts: structuredClone(defaultPartsForKind)
        }
      ];
    }

    const mappedConfig: Config = {
      feedback: [],
      kiService: false,
      playerSettings: null,
      resolutions: [1200, 800, 600, 100],
      sessionTimeoutMinutes: { value: 60 },
      // Cast auf den Union-Type
      exerciseSubKind: examcise.config?.exerciseSubKind as ExerciseSubKind,
      materialTabName: (examcise as ExamEntity | ExerciseEntity).config?.materialTabName
    };

    const mappedLernItem: LearnItem = {
      id: examcise.id,
      subjectId: examcise.subjectId,
      consumerId: examcise.consumerId,
      name: examcise.name,
      kind: (examcise as ExamEntity | ExerciseEntity).kind,
      assignments: ((examcise as ExamEntity | ExerciseEntity).assignments as unknown[]) || [],
      assets: examcise.assets,
      pages: mappedPages,
      correct: (examcise as ExamEntity | ExerciseEntity).correct ? ((examcise as ExamEntity | ExerciseEntity).correct as unknown as Correct[]) : [],
      config: mappedConfig,
      copyRestricted: Boolean(examcise.copyRestricted),
      consumerData: examcise.consumerData || {},
      approval: examcise.approval as Approval,
      updated: examcise.updated ? new Date(examcise.updated) : new Date(),
      changedBy: examcise.changedBy || '',
      lastVersion: (examcise as ExamEntity | ExerciseEntity).lastVersion || '1.0',
      version: examcise.version || 1,
      dateCreated: new Date().toISOString(),
      wrongThings: [],
      isExam: this.dialogData.type === 'exam'
    };

    this.playerContentMessage.learnItem = mappedLernItem;

    let loadingCount = 2;
    const checkAndReady = () => {
      loadingCount--;
      if (loadingCount === 0) {
        this.isPlayerMessageReady.set(true);
        if (this.playerService.iFrameReady) {
          this.postMessage2Player();
        }
      }
    };

    this.playerService.getPlayerSettings$(examcise.consumerId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (settings) => {
          this.playerContentMessage.config.playerSettings = settings as PlayerSettings;
          checkAndReady();
        },
        error: () => checkAndReady()
      });

    this.playerService.getConsumer$(examcise.consumerId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (consumer) => {
          this.playerContentMessage.config.kiService = Boolean(consumer?.kiService);
          checkAndReady();
        },
        error: () => checkAndReady()
      });
  }

  private handlePlayerEvents(message: IFrameMessage): void {
    if (message.kind === 'MessageHubInitialized') {
      if (this.isPlayerMessageReady()) {
        console.log('::==> Player Ready empfangen. Warte 1,5s vor postMessage... <==::');
        setTimeout(() => {
          this.playerService.iFrameReady = true;
          console.log('::==> Pause vorbei, sende Initial-Daten an Player <==::');
          this.postMessage2Player();
        }, 1500);
      }
      return;
    }

    if (message.kind === 'EditPart') {
      const isAlreadyOpen =
        this.editorPaneVisible() &&
        this.playerService.pageIndex === message.pageIndex &&
        this.playerService.index === message.index;

      this.playerService.pageIndex = message.pageIndex;
      this.playerService.index = message.index;

      // Toggle: same part already open → close editor (X button clicked)
      if (isAlreadyOpen) {
        this.editorPaneVisible.set(false);
        this.activeEditorComponent.set(null);
        this.activeEditorInputs.set({});
        return;
      }

      const part = this.playerContentMessage.learnItem.pages[this.playerService.pageIndex].parts[this.playerService.index];

      if (part) {
        switch (part._t) {
          case 'TextPart':
            this.explanationContentHtml = part.markup || '';
            this.showAddExerciseButton.set(false);
            this.activeEditorComponent.set(null);
            this.activeEditorInputs.set({});
            this.editorPaneVisible.set(true);
            break;
          case 'EnumerationPart':
            if (part.name === 'enum-ph-mark-gap-text') {
              // Always create a shallow copy of data and config to avoid mutating a frozen NGXS state object.
              // On first open config is null → becomes {}; on subsequent opens the existing config is copied.
              this.data = { ...this.data, config: { ...(this.data.config ?? {}) } } as ExamEntity | ExerciseEntity | ExplanationEntity;
              this.showAddExerciseButton.set(false);
              this.activeEditorComponent.set(FreeInputTextEditorComponent);
              this.activeEditorInputs.set({
                part: part as FreeInputTextPart,
                exerciseConfig: this.data.config,
                kiService: this.playerContentMessage.config.kiService,
                authToken: this.authToken,
                assetsUrl: this.assetsUrl,
                mediaUrl: this.mediaUrl,
                playerSettings: this.playerContentMessage.config.playerSettings ?? null,
              });
              this.editorPaneVisible.set(true);
            }
            else {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              this.explanationContentHtml = (part as any).text ?? '';
              this.showAddExerciseButton.set(true);
              this.activeEditorComponent.set(null);
              this.activeEditorInputs.set({});
              this.editorPaneVisible.set(true);
            }
            break;
          case 'EnumPlaceholderPart':
            this.explanationContentHtml = part.text || '';
            this.showAddExerciseButton.set(true);
            this.activeEditorComponent.set(null);
            this.activeEditorInputs.set({});
            this.editorPaneVisible.set(true);
            break;
          default:
            alert(`Unsupported _t: ${part._t}`);
            break;
        }
      }
    }
  }

  private postMessage2Player(): void {
    if (!this.isPlayerMessageReady()) return;

    if (this.dialogData.type === 'explanation') {
      const explanation = this.examcise as ExplanationEntity;
      const explanationMessage = {
        kind: 'Explanation' as const,
        learnItem: {
          id: explanation.id,
          name: explanation.name,
          subjectId: explanation.subjectId,
          pages: this.playerContentMessage.learnItem.pages.map((page: Pages) => ({
            layout: page.layout,
            parts: page.parts.map((part: LearnItemPart) => ({
              name: (part as TextPart).name,
              markup: (part as TextPart).markup
            }))
          })),
          consumerData: explanation.consumerData || {}
        },
        editable: true,
        config: this.playerContentMessage.config
      };
      this.playerService.postMessageToPlayer(
        this.playerService.iFramePlayerPreviewUrl(this.data.consumerId),
        explanationMessage
      );
      return;
    }

    this.playerService.postMessageToPlayer(this.playerService.iFramePlayerPreviewUrl(this.data.consumerId), this.playerContentMessage);
  }

  public showQuantityErrorsTab(): boolean {
    if (this.dialogData.type === 'exam' || this.dialogData.type === 'explanation') {
      return false;
    }
    const cfg = exerciseTypeConfig[(this.data as ExamEntity | ExerciseEntity)?.kind ?? ''];
    return !cfg || cfg.quantityErrors !== false;
  }

  public showAnyAdditionalTab(): boolean {
    return this.showQuantityErrorsTab();
  }

  private initQuantityErrors(): void {
    const stored = ((this.data as ExamEntity | ExerciseEntity)?.quantityErrors ?? []) as QuantityError[];
    if (!stored.length) {
      this.quantityErrorRanges.set(DEFAULT_QUANTITY_ERROR_RANGES.map(r => ({ ...r })));
      return;
    }
    const sorted = [...stored].sort((a, b) => a.from - b.from);
    const result: QuantityError[] = [];
    let cursor = 0;
    for (const range of sorted) {
      if (range.from > cursor) {
        result.push({ from: cursor, to: range.from - 1, message: '' });
      }
      result.push({ ...range });
      cursor = range.to + 1;
    }
    if (cursor <= 100) {
      result.push({ from: cursor, to: 100, message: '' });
    }
    this.quantityErrorRanges.set(result);
  }

  public onRangeActive(range: QuantityError, index: number): void {
    this.activeRangeIndex.set(index);
    this.messageText.set(range.message);
  }

  public isSaveMessageDisabled(): boolean {
    const idx = this.activeRangeIndex();
    if (idx === null) {
      return true;
    }
    const msg = this.messageText();
    const active = this.quantityErrorRanges()[idx];
    return !msg || msg === active.message;
  }

  public saveMessage(): void {
    const idx = this.activeRangeIndex();
    if (idx === null) {
      return;
    }
    const ranges = [...this.quantityErrorRanges()];
    ranges[idx] = { ...ranges[idx], message: this.messageText() };
    this.quantityErrorRanges.set(ranges);
    (this.data as ExerciseEntity).quantityErrors = ranges.filter(r => r.message.trim() !== '');
  }

  public getLeftAreaSize(): number {
    return this.editorPaneVisible() ? 50 : 100;
  }

  public getLeftAreaMinSize(): number {
    return this.editorPaneVisible() ? 5 : 0;
  }

  public onIFrameLoaded(): void {
    if (this.playerPreviewIframe && this.playerPreviewIframe.nativeElement.contentWindow) {
      this.playerService.iFrameReady = true;
      this.playerService.playerPreviewIFrame = this.playerPreviewIframe;
      if (this.isPlayerMessageReady()) {
        this.postMessage2Player();
      }
    }
  }

  public onPreview(): void {
    const pagesForBackend = this.playerContentMessage.learnItem.pages.map((page: Pages) => {
      const { _t, parts, baseLayout, config, ...rest } = page;
      const result: Record<string, unknown> = {
        ...rest,
        _t: _t,
        parts: parts.map((part: LearnItemPart) => {
          const { _t: partClass, ...partRest } = part;
          return { ...partRest, _t: partClass };
        })
      };
      if (baseLayout != null) {
        result.baseLayout = baseLayout;
      }
      if (config != null && Object.keys(config as Record<string, unknown>).length > 0) {
        result.config = config;
      }
      return result;
    });

    const previewData = {
      ...this.data,
      pages: pagesForBackend
    };

    this.previewApiService.upsertPreview(this.dialogData.type, this.data.id, previewData)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.playerService.openPlayerPreview(this.data.consumerId, this.dialogData.type, this.data.id),
        error: () => this.widgetMessageService.setMessage({
          message: this.translocoService.translate('preview.saveError'),
          messageTye: 'mtError'
        })
      });
  }

  public addPage(): void {
    const defaultParts = getExplanationParts(this.translocoService).map(p => ({ ...p }));
    this.playerContentMessage.learnItem.pages.push({
      _t: 'SimpleLearnItemPage' as const,
      layout: '1col',
      baseLayout: null,
      showMarginalColumnOnMobile: null,
      config: {},
      parts: defaultParts,
    } as Pages);
    const newIndex = this.playerContentMessage.learnItem.pages.length - 1;
    this.currentPageIndex.set(newIndex);
    this.postMessage2Player();
    this.goToPage(newIndex);
  }

  public goToPage(index: number): void {
    const pages = this.playerContentMessage.learnItem.pages;
    if (index < 0 || index >= pages.length) {
      return;
    }
    this.editorPaneVisible.set(false);
    this.activeEditorComponent.set(null);
    this.activeEditorInputs.set({});
    this.currentPageIndex.set(index);

    // changes the x symbol back to pen icon
    this.playerService.postMessageToPlayer(
      this.playerService.iFramePlayerPreviewUrl(this.data.consumerId),
      { kind: 'MarkdownEditorClosed' }
    );

    this.playerService.postMessageToPlayer(
      this.playerService.iFramePlayerPreviewUrl(this.data.consumerId),
      { kind: 'ExplanationPageIndex', pageIndex: index }
    );
  }

  public deletePage(): void {
    const pages = this.playerContentMessage.learnItem.pages;
    if (pages.length <= 1) {
      return;
    }
    pages.splice(this.currentPageIndex(), 1);
    const newIndex = Math.min(this.currentPageIndex(), pages.length - 1);
    this.currentPageIndex.set(newIndex);
    this.postMessage2Player();
    this.goToPage(newIndex);
  }

  public changeLayout(newLayout: string): void {
    const page = this.playerContentMessage.learnItem.pages[this.currentPageIndex()];
    if (!page || page.layout === newLayout) {
      return;
    }
    const oldLayout = page.layout ?? '1col';
    const mapping = EXPLANATION_LAYOUT_TRANSITION[oldLayout]?.[newLayout] ?? {};
    const newPartDefs = getExplanationPartsByLayout(this.translocoService)[newLayout];
    if (!newPartDefs) {
      return;
    }
    const oldPartsByName: Record<string, TextPart> = {};
    for (const part of page.parts as TextPart[]) {
      oldPartsByName[(part as TextPart).name] = part as TextPart;
    }
    page.parts = newPartDefs.map((def: TextPart) => {
      if (oldPartsByName[def.name]) {
        return { ...oldPartsByName[def.name] };
      }
      const sourceName = Object.entries(mapping).find(([, target]) => target === def.name)?.[0];
      if (sourceName && oldPartsByName[sourceName]) {
        return { ...oldPartsByName[sourceName], name: def.name };
      }
      return { ...def };
    });
    page.layout = newLayout;
    this.postMessage2Player();
  }

  public toggleMarginalColumn(): void {
    const page = this.playerContentMessage.learnItem.pages[this.currentPageIndex()];
    if (!page) {
      return;
    }
    page.showMarginalColumnOnMobile = !page.showMarginalColumnOnMobile;
  }

  public openPagesDialog(): void {
    const dialogRef = this.dialog.open(PagesDialogComponent, {
      width: '480px',
      data: { pages: this.playerContentMessage.learnItem.pages } as PagesDialogData
    });
    dialogRef.afterClosed().subscribe((newPages: Pages[] | undefined) => {
      if (!newPages) {
        return;
      }
      this.playerContentMessage.learnItem.pages = newPages;
      const safeIndex = Math.min(this.currentPageIndex(), newPages.length - 1);
      this.currentPageIndex.set(safeIndex);
      this.postMessage2Player();
      this.goToPage(safeIndex);
    });
  }

  public onCancel(): void {
    this.dialogRef.close();
  }

  public onSave(): void {
    const pagesForBackend = this.playerContentMessage.learnItem.pages.map((page: Pages) => {
      const { _t, parts, baseLayout, config, ...rest } = page;
      const result: Record<string, unknown> = {
        ...rest,
        _t: _t,
        parts: parts.map((part: LearnItemPart) => {
          const { _t: partClass, ...partRest } = part;
          return { ...partRest, _t: partClass };
        })
      };
      if (baseLayout != null) {
        result.baseLayout = baseLayout;
      }
      if (config != null && Object.keys(config as Record<string, unknown>).length > 0) {
        result.config = config;
      }
      return result;
    });

    const updatedModel = {
      ...this.data,
      pages: pagesForBackend as unknown as string[]
    };

    this.dialogRef.close(updatedModel);
  }

  public callbackFct(ev: string): void {
    this.explanationContentHtml = ev;
    const currentPart = this.playerContentMessage.learnItem.pages[this.playerService.pageIndex].parts[this.playerService.index];

    if (currentPart) {
      if (currentPart._t === 'TextPart') {
        currentPart.markup = this.explanationContentHtml;
        currentPart.markdown = this.turndownService.turndown(this.explanationContentHtml);
      }
      else if (currentPart._t === 'EnumerationPart' || currentPart._t === 'EnumPlaceholderPart') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (currentPart as any).text = this.explanationContentHtml;
      }
    }
    this.postMessage2Player();
  }
}
